import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import assetService from "../../services/Assetservice";

const initialState = {
  assets: [],
  summary: {
    totalAssets: 0,
    activeAssets: 0,
    returned: 0,
    lostDamaged: 0,
  },
  status: "idle",
  error: null,

  history: {
    items: [],
    pagination: null,
    status: "idle",
    error: null,
  },

  queryModal: {
    open: false,
    assetId: null,
  },
  queryStatus: "idle",
  queryError: null,
};

export const fetchMyAssets = createAsyncThunk(
  "myAssets/fetchMyAssets",
  async (params, { rejectWithValue }) => {
    try {
      return await assetService.getMyAssets(params);
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to load your assets."
      );
    }
  }
);

export const fetchAssetSummary = createAsyncThunk(
  "myAssets/fetchAssetSummary",
  async (params, { rejectWithValue }) => {
    try {
      return await assetService.getAssetSummary(params);
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to load summary."
      );
    }
  }
);

export const raiseAssetQuery = createAsyncThunk(
  "myAssets/raiseAssetQuery",
  async ({ assetId, message }, { rejectWithValue }) => {
    try {
      return await assetService.raiseAssetQuery({ assetId, message });
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to raise query."
      );
    }
  }
);

const myAssetsSlice = createSlice({
  name: "myAssets",
  initialState,
  reducers: {
    openQueryModal: (state, action) => {
      state.queryModal = { open: true, assetId: action.payload };
      state.queryStatus = "idle";
      state.queryError = null;
    },
    closeQueryModal: (state) => {
      state.queryModal = { open: false, assetId: null };
      state.queryStatus = "idle";
      state.queryError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchMyAssets
      .addCase(fetchMyAssets.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMyAssets.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.assets = action.payload.data || [];
      })
      .addCase(fetchMyAssets.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })

      // fetchAssetSummary
      .addCase(fetchAssetSummary.pending, (state) => {
        // no loading state needed for summary
      })
      .addCase(fetchAssetSummary.fulfilled, (state, action) => {
        const d = action.payload.data || {};
        state.summary = {
          totalAssets:  d.total       ?? 0,
          activeAssets: d.active      ?? 0,
          returned:     d.returned    ?? 0,
          lostDamaged:  d.lostDamaged ?? 0,
        };
      })
      .addCase(fetchAssetSummary.rejected, (state, action) => {
        // silently fail — summary is non-critical
      })

      // raiseAssetQuery
      .addCase(raiseAssetQuery.pending, (state) => {
        state.queryStatus = "loading";
        state.queryError = null;
      })
      .addCase(raiseAssetQuery.fulfilled, (state) => {
        state.queryStatus = "succeeded";
        state.queryModal = { open: false, assetId: null };
      })
      .addCase(raiseAssetQuery.rejected, (state, action) => {
        state.queryStatus = "failed";
        state.queryError = action.payload || action.error.message;
      });
  },
});

export const { openQueryModal, closeQueryModal } = myAssetsSlice.actions;
export default myAssetsSlice.reducer;